/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import toast from 'react-hot-toast';
import _ from 'lodash';
import { Actions, Async, F1 } from '@client/new/helpers/types';
import {
  CreateInfluencerInput,
  useCreateInfluencerMutation,
} from '../../../../generated/graphql';
import { Control, FieldError, UseFormRegister, useForm } from 'react-hook-form';
import { muiForm } from '@client/new/helpers/forms';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import ajax, { FreeSubscriptionType } from '@client/components/ajax';
import { PATHS } from '@client/routes';
import { navigate, goBack } from '@client/new/helpers/history';
import { instagramUsernameValidation } from '../commonValidation';

export type NewCreatorViewModel = {
  dispatch: NewCreatorViewDispatcher;
  submitting: boolean;
  register: UseFormRegister<InitialForm>;
  control: Control<InitialForm, any, InitialForm>;
  hasFreeSubscription: boolean;
  onboardingEnabled: boolean;
  tier: {
    error: FieldError | undefined;
    value: number | null;
    onChange: (value: number) => void;
  };
};

export type NewCreatorViewActionsMap = {
  submit: null;
  cancel: null;
};

export type NewCreatorViewActions = Actions<NewCreatorViewActionsMap>;

export type NewCreatorViewDispatcher = F1<NewCreatorViewActions>;

const email = z
  .string()
  .trim()
  .nonempty('Field is required')
  .email('This is not a valid email');

export const newCreatorForm = z
  .object({
    email,
    repeatEmail: email,
    firstName: z.string().trim().nonempty('Field is required'),
    lastName: z.string().trim().nonempty('Field is required'),
    tier: z
      .number()
      .nullable()
      .transform((value, ctx): number => {
        if (value == null)
          ctx.addIssue({
            code: 'custom',
            message: 'Field is required',
          });
        return value ?? NaN;
      }),
    profileInstagram: instagramUsernameValidation,
    freeSubscription: z.boolean(),
    onboardingEnabled: z.boolean(),
  })
  .superRefine(({ email, repeatEmail }, ctx) => {
    if (email !== repeatEmail) {
      ctx.addIssue({
        code: 'custom',
        path: ['repeatEmail'],
        message: `Email and Repeat Email must match`,
      });
    }
  });

export type InitialForm = {
  email: string;
  repeatEmail: string;
  firstName: string;
  lastName: string;
  profileInstagram: string;
  tier: number | null;
  freeSubscription: boolean;
  onboardingEnabled: boolean;
};

const mapFormToData = (values: InitialForm): CreateInfluencerInput | null => {
  if (!values.tier) {
    return null;
  }
  return {
    email: values.email.trim().toLowerCase(),
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    profileInstagram: values.profileInstagram.trim().toLowerCase(),
    onboardingEnabled: values.onboardingEnabled,
    tier: values.tier,
  };
};

export const useNewCreatorViewModel = (): Async<NewCreatorViewModel> => {
  const [submitting, setSubmitting] = useState(false);
  const [createInfluencerMutation] = useCreateInfluencerMutation();

  const { register, handleSubmit, control, watch, formState, setValue } =
    muiForm(
      useForm<InitialForm, any, InitialForm>({
        defaultValues: {
          email: '',
          repeatEmail: '',
          firstName: '',
          lastName: '',
          profileInstagram: '',
          tier: null,
          freeSubscription: true,
          onboardingEnabled: false,
        },
        resolver: zodResolver(newCreatorForm),
      }),
    );

  const onSubmit = handleSubmit(async (values) => {
    const data = mapFormToData(values);
    if (!data) {
      toast.error('Input data is invalid!');
      return;
    }
    setSubmitting(true);
    try {
      if (values.freeSubscription) {
        await ajax.generateFreeSubscription({
          email: data.email,
          subscriptionType: FreeSubscriptionType.ANNUAL,
          note: 'Free Creator Subscription',
        });
      }

      const res = await createInfluencerMutation({
        variables: {
          data,
        },
      });
      navigate(PATHS.PARTNER_EDIT(res.data?.createInfluencer.id));
      toast.success('New creator added successfully!');
    } catch (e: any) {
      console.error(e);
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  });

  const dispatch: NewCreatorViewDispatcher = (action) => {
    switch (action.type) {
      case 'submit':
        onSubmit();
        return null;
      case 'cancel':
        goBack();
        return null;
    }
  };

  const hasFreeSubscription = watch('freeSubscription');
  const onboardingEnabled = watch('onboardingEnabled');
  const tierError = formState.errors.tier;
  const tier = watch('tier');
  return {
    type: 'fetched',
    value: {
      dispatch,
      submitting,
      control,
      register,
      hasFreeSubscription,
      onboardingEnabled,
      tier: {
        error: tierError,
        value: tier,
        onChange: (value: number) => setValue('tier', value),
      },
    },
  };
};
